import {useCallback} from 'react';
import {sendToStatist} from 'packages/statist';

import {FormatBoldNewIcon} from '@time-webkit/all/icons/format-bold-new';
import {FormatItalicNewIcon} from '@time-webkit/all/icons/format-italic-new';
import {FormatStrikeNewIcon} from '@time-webkit/all/icons/format-strike-new';
import {FormatHeaderNewIcon} from '@time-webkit/all/icons/format-header-new';
import {CodeBlockNewIcon} from '@time-webkit/all/icons/code-block-new';
import {FormatQuoteOpenNewIcon} from '@time-webkit/all/icons/format-quote-open-new';
import {FormatListBulletedNewIcon} from '@time-webkit/all/icons/format-list-bulleted-new';
import {FormatListNumberedNewIcon} from '@time-webkit/all/icons/format-list-numbered-new';

import {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import {useEditorContext} from '../../context';
import {Divider} from '../divider';
import {ShortcutTooltip} from '../shortcut-tooltip';
import {ActionButton} from '../action-button';

import {OverflowContainer} from '../overflow-container';

import styles from './styles.module.css';

interface FormattingBarProps {
    outerRef?: React.RefObject<HTMLDivElement>;
    onClosePopover?: (event: MouseEvent) => void;
}

export const FormattingBar = ({outerRef, onClosePopover}: FormattingBarProps) => {
    const {editor, features} = useEditorContext();
    const {codeBlock} = features;

    const handleBoldClick = useCallback(() => {
        editor?.chain().focus().toggleMark('bold').run();
        sendToStatist('editor.format.tap', {formatType: 'bold', hotkey: false});
    }, [editor]);
    const handleItalicClick = useCallback(() => {
        editor?.chain().focus().toggleMark('italic').run();
        sendToStatist('editor.format.tap', {formatType: 'italic', hotkey: false});
    }, [editor]);
    const handleStrikeClick = useCallback(() => {
        editor?.chain().focus().toggleMark('strike').run();
        sendToStatist('editor.format.tap', {formatType: 'strikethrough', hotkey: false});
    }, [editor]);
    const handleHeaderClick = useCallback(() => {
        editor?.chain().focus().toggleHeading({level: 3}).run();
        sendToStatist('editor.format.tap', {formatType: 'header', hotkey: false});
    }, [editor]);
    const handleCodeBlockClick = useCallback(() => {
        editor?.chain().focus().toggleCodeBlock().run();
        sendToStatist('editor.format.tap', {formatType: 'code_block', hotkey: false});
    }, [editor]);
    const handleQuoteClick = useCallback(() => {
        editor?.chain().focus().toggleBlockquote().run();
        sendToStatist('editor.format.tap', {formatType: 'quote', hotkey: false});
    }, [editor]);
    const handleOrderedListClick = useCallback(() => {
        editor?.chain().focus().toggleOrderedList().run();
        sendToStatist('editor.format.tap', {formatType: 'list_ordered', hotkey: false});
    }, [editor]);
    const handleBulletListClick = useCallback(() => {
        editor?.chain().focus().toggleBulletList().run();
        sendToStatist('editor.format.tap', {formatType: 'list_bulleted', hotkey: false});
    }, [editor]);

    return (
        <OverflowContainer
            popoverTrigger={outerRef}
            placement='top right'
            className={styles.bar}
            data-testid='editor_formatting_bar'
            onClose={onClosePopover}
        >
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownBold}>
                <ActionButton
                    aria-label='Bold'
                    isActive={editor?.isActive('bold')}
                    onPress={handleBoldClick}
                    icon={<FormatBoldNewIcon />}
                    data-qa='editor_button_bold'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownItalic}>
                <ActionButton
                    aria-label='Italic'
                    isActive={editor?.isActive('italic')}
                    onPress={handleItalicClick}
                    icon={<FormatItalicNewIcon />}
                    data-qa='editor_button_italic'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownStrike}>
                <ActionButton
                    aria-label='Strike'
                    isActive={editor?.isActive('strike')}
                    onPress={handleStrikeClick}
                    icon={<FormatStrikeNewIcon />}
                    data-qa='editor_button_strike'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownHeading}>
                <ActionButton
                    aria-label='Heading'
                    isActive={editor?.isActive('heading', {level: 3})}
                    onPress={handleHeaderClick}
                    icon={<FormatHeaderNewIcon />}
                    data-qa='editor_button_heading'
                />
            </ShortcutTooltip>
            {codeBlock && <Divider />}
            {codeBlock && <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownCodeBlock}>
                <ActionButton
                    aria-label='Code Block'
                    isActive={editor?.isActive('codeBlock')}
                    onPress={handleCodeBlockClick}
                    icon={<CodeBlockNewIcon />}
                    data-qa='editor_button_codeBlock'
                />
            </ShortcutTooltip>}
            <Divider />
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownOrderedList}>
                <ActionButton
                    aria-label='Ordered List'
                    isActive={editor?.isActive('orderedList')}
                    onPress={handleOrderedListClick}
                    icon={<FormatListNumberedNewIcon />}
                    data-qa='editor_button_orederedList'
                />
            </ShortcutTooltip>
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownBulletList}>
                <ActionButton
                    aria-label='Bullet List'
                    isActive={editor?.isActive('bulletList')}
                    onPress={handleBulletListClick}
                    icon={<FormatListBulletedNewIcon />}
                    data-qa='editor_button_bulletList'
                />
            </ShortcutTooltip>
            <Divider />
            <ShortcutTooltip descriptor={KEYBOARD_SHORTCUTS.msgMarkdownQuote}>
                <ActionButton
                    aria-label='Block Quote'
                    isActive={editor?.isActive('blockquote')}
                    onPress={handleQuoteClick}
                    icon={<FormatQuoteOpenNewIcon />}
                    data-qa='editor_button_blockquote'
                />

            </ShortcutTooltip>
        </OverflowContainer>
    );
};
